/**
 * Line Items step header with spreadsheet import — CR-6204.
 *
 * Renders the step title, the Download Template / Import from Spreadsheet
 * buttons beside the host's own buttons (Add Item), and, after an import, a
 * summary of what was added, what was already there and which rows were
 * skipped and why. Imported rows land in the wizard as ordinary lines,
 * editable before the user continues.
 */
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LineItemForm } from './rfqWizardTypes';
import {
  IMPORT_ACCEPT,
  downloadLineItemTemplate,
  mergeImportedLines,
  parseLineItemFile,
} from './rfqLineItemImport';
import type { RowProblem } from './rfqLineItemImport';
import { Button } from '../ui/button';
import { AlertCircle, CheckCircle, Download, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

/** Skipped rows listed before the rest are summarised as "…and N more". */
const PROBLEMS_SHOWN = 10;

interface LineItemsImportHeaderProps {
  /** The wizard's current lines — imports are added after them. */
  lineItems: LineItemForm[];
  /** Replace the wizard's lines with the merged list. */
  onReplace: (lines: LineItemForm[]) => void;
  /** The host's own header buttons, rendered after the import buttons. */
  children?: ReactNode;
}

interface ImportSummary {
  fileName: string;
  added: number;
  alreadyPresent: number;
  problems: RowProblem[];
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Read a File's bytes (FileReader keeps it working where Blob.arrayBuffer is missing). */
function readFile(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(file);
  });
}

export default function LineItemsImportHeader({ lineItems, onReplace, children }: LineItemsImportHeaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const handleDownload = () => {
    try {
      downloadLineItemTemplate();
    } catch (err: unknown) {
      console.error('Line item template download failed:', err);
      toast.error('The template could not be downloaded.');
    }
  };

  const handleFile = async (file: File) => {
    setReading(true);
    try {
      const result = parseLineItemFile(await readFile(file), file.name);
      if (!result.ok) {
        setSummary(null);
        toast.error(result.error);
        return;
      }
      const merged = mergeImportedLines(lineItems, result.lines);
      if (merged.added > 0) {
        onReplace(merged.lines);
        toast.success(`${plural(merged.added, 'line', 'lines')} imported`);
      }
      setSummary({ fileName: file.name, added: merged.added, alreadyPresent: merged.alreadyPresent, problems: result.problems });
    } catch (err: unknown) {
      console.error('Line item import failed:', err);
      toast.error("The file couldn't be read. Save it as .xlsx or .csv and try again.");
    } finally {
      setReading(false);
    }
  };

  const skipped = summary?.problems.length ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <h3 className="font-semibold">Line Items</h3>
        <div className="flex gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" /> Download Template
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={reading}>
            {reading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            Import from Spreadsheet
          </Button>
          {children}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={IMPORT_ACCEPT}
          className="hidden"
          aria-label="Line items spreadsheet"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Clear so choosing the same file again (after fixing it) still fires.
            e.target.value = '';
            if (file) void handleFile(file);
          }}
        />
      </div>

      {summary && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-2" role="status">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              {summary.added > 0 ? (
                <p className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                  <span>
                    {plural(summary.added, 'line', 'lines')} imported from {summary.fileName} — check them below
                    before you continue.
                  </span>
                </p>
              ) : (
                <p className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  <span>No lines imported from {summary.fileName}.</span>
                </p>
              )}
              {summary.alreadyPresent > 0 && (
                <p className="text-muted-foreground pl-6">
                  {plural(summary.alreadyPresent, 'row was', 'rows were')} already in this RFQ and not added again.
                </p>
              )}
            </div>
            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSummary(null)} aria-label="Dismiss import summary">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {skipped > 0 && summary && (
            <div className="text-amber-700 dark:text-amber-500">
              <p className="font-medium">
                {plural(skipped, 'row', 'rows')} skipped. Fix {skipped === 1 ? 'it' : 'them'} and import the file
                again (lines already added are not added twice), or add {skipped === 1 ? 'it' : 'them'} by hand:
              </p>
              <ul className="mt-1 space-y-0.5 list-disc pl-5">
                {summary.problems.slice(0, PROBLEMS_SHOWN).map((p) => (
                  <li key={p.row}>Row {p.row}: {p.messages.join('; ')}</li>
                ))}
              </ul>
              {skipped > PROBLEMS_SHOWN && <p className="mt-1">…and {skipped - PROBLEMS_SHOWN} more.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
